import { useState } from 'react'
import { useAddContacts } from '@/api/hooks/use-audiences'

interface AudienceManualAddProps {
  audienceId: string
}

export function AudienceManualAdd({ audienceId }: AudienceManualAddProps): React.JSX.Element {
  const [email, setEmail] = useState('')
  const [metadataJson, setMetadataJson] = useState('{}')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const addContacts = useAddContacts()

  function handleAdd(e: React.FormEvent): void {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    if (!email.trim()) {
      setError('Email is required.')
      return
    }

    let metadata: Record<string, unknown> = {}
    try {
      metadata = JSON.parse(metadataJson)
    } catch {
      setError('Invalid JSON in metadata field.')
      return
    }

    addContacts.mutate(
      { audience_id: audienceId, contacts: [{ email: email.trim(), metadata }] },
      {
        onSuccess: () => {
          setEmail('')
          setMetadataJson('{}')
          setSuccess(true)
        },
        onError: () => setError('Failed to add contact.'),
      },
    )
  }

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-4">
      <h3 className="text-sm font-medium text-text-primary">Add a single contact</h3>
      <form onSubmit={handleAdd} className="mt-3 space-y-3">
        <div>
          <label htmlFor="manual-email" className="block text-xs font-medium text-text-secondary">
            Email
          </label>
          <input
            id="manual-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="subscriber@example.com"
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none"
          />
        </div>
        <div>
          <label
            htmlFor="manual-metadata"
            className="block text-xs font-medium text-text-secondary"
          >
            Metadata (JSON)
          </label>
          <textarea
            id="manual-metadata"
            value={metadataJson}
            onChange={(e) => setMetadataJson(e.target.value)}
            rows={3}
            spellCheck={false}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-1 px-3 py-2 font-mono text-sm text-text-primary focus:border-brand-500 focus:outline-none"
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && <p className="text-sm text-green-600">Contact added successfully.</p>}

        <button
          type="submit"
          disabled={addContacts.isPending}
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {addContacts.isPending ? 'Adding...' : 'Add Contact'}
        </button>
      </form>
    </div>
  )
}
